import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, IconEyeOff } from '../../../base/icons';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { amIHeldBackstage, amIInBackstage, amIStageParticipant, bringAllBackstageLive, goBackstage, goLive, isBackstageModeEnabled } from '../../../webinar';
import BackstageRoster from './BackstageRoster';
import { useModerationStyles } from './styles';
/**
 * The self-service backstage card for stage occupants (moderators and
 * panelists). Backstage is a separate '-backstage' room of the meeting; this
 * switches the local participant between it and the main room, keeping devices,
 * mute states and the virtual background.
 *
 * Only offered when the meeting has backstage switched on
 * (meeting.settings.ENABLE_BACKSTAGE_MODE in the token). The one exception is
 * somebody who is already backstage: they need to be told where they are.
 *
 * A guest a moderator sent backstage sees the card without a Go Live button:
 * the server holds them there until a moderator brings them live, and a
 * button that only ever fails would be worse than none.
 *
 * Main-room moderators additionally get the roster of who is waiting
 * backstage, with Bring live and the other per-person controls (and, past a
 * few people, the full backstage dialog), plus Bring all live.
 *
 * @returns {JSX.Element | null}
 */
const BackstageSection = () => {
    const { t } = useTranslation();
    const { classes, cx } = useModerationStyles();
    const dispatch = useDispatch();
    const isStage = useSelector(amIStageParticipant);
    const isModerator = useSelector(isLocalParticipantModerator);
    const inBackstage = useSelector(amIInBackstage);
    const held = useSelector(amIHeldBackstage);
    const enabled = useSelector(isBackstageModeEnabled);
    const onClick = useCallback(() => dispatch(inBackstage ? goLive() : goBackstage()), [dispatch, inBackstage]);
    const onBringAllLive = useCallback(() => dispatch(bringAllBackstageLive()), [dispatch]);
    // Stage occupants get the self-service control. Anyone else only sees it
    // once a moderator has actually put them backstage, so they know where
    // they are.
    if ((!enabled || !isStage) && !inBackstage) {
        return null;
    }
    let description;
    if (held) {
        description = t('webinar.backstageHeldDescription');
    }
    else if (inBackstage) {
        description = t('webinar.backstageDescription');
    }
    else {
        description = t('webinar.toggleHint');
    }
    return (React.createElement("div", { className: cx(classes.card, inBackstage && classes.cardHighlighted) },
        React.createElement("div", { className: classes.cardHeader },
            React.createElement("div", { className: cx(classes.cardIcon, inBackstage && classes.cardIconWarning) },
                React.createElement(Icon, { color: inBackstage ? '#B58900' : '#4B6790', size: 16, src: IconEyeOff })),
            React.createElement("span", { className: classes.cardTitle }, t('webinar.toggleLabel')),
            React.createElement("span", { className: cx(classes.badge, !inBackstage && classes.badgeLive) }, inBackstage ? t('webinar.inBackstage') : t('webinar.liveBadge'))),
        React.createElement("p", { className: classes.cardDescription }, description),
        !held && (React.createElement("button", { className: cx(classes.button, inBackstage ? classes.buttonDanger : classes.buttonPrimary), id: 'backstage-pane-switch', onClick: onClick, type: 'button' }, inBackstage
            ? t('webinar.goLive')
            : t('participantsPane.actions.goBackstage'))),
        isModerator && !inBackstage && React.createElement(BackstageRoster, null),
        isModerator && !inBackstage && (React.createElement("button", { "aria-label": t('webinar.bringAllLiveHint'), className: cx(classes.button, classes.buttonOutlined), id: 'backstage-pane-bring-all-live', onClick: onBringAllLive, title: t('webinar.bringAllLiveHint'), type: 'button' }, t('webinar.bringAllLive')))));
};
export default BackstageSection;
